import React, { useState, useMemo } from 'react';
import { Star, ExternalLink, Link2, X, AlertCircle, RefreshCw, TrendingDown, Calendar, CheckCircle, Info, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { useGoogleBusinessSummary } from '../../hooks/useGoogleBusinessSummary';
import { useGoogleReviews, useMarkReviewResponded } from '../../hooks/useGoogleReviews';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { supabase } from '../../lib/supabase';
import { FeatureRequestButton } from './FeatureRequestButton';
import { AddressUpdateFlow } from './AddressUpdateFlow';
import { useToast } from '../ui/Toast';
import { useQueryClient } from '@tanstack/react-query';
import { useGoogleReviewEnhancementToggle, useToggleGoogleReviewEnhancement } from '../../hooks/useGoogleReviewEnhancementToggle';

export function GoogleReviewsSection() {
  const { data: companyId } = useCurrentCompany();
  const { data: businessSummary, isLoading: summaryLoading } = useGoogleBusinessSummary();
  const { data: reviews, isLoading: reviewsLoading } = useGoogleReviews(true); // Show actionable only
  const { data: allReviews } = useGoogleReviews(false); // Get all reviews for analysis
  const markResponded = useMarkReviewResponded();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [showAddressFlow, setShowAddressFlow] = useState(false);
  const [showIncorrectInfoModal, setShowIncorrectInfoModal] = useState(false);
  const [checkingPlaceId, setCheckingPlaceId] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasPlaceId, setHasPlaceId] = useState<boolean | null>(null);
  const { data: isEnhancementEnabled = false, isLoading: isLoadingToggle } = useGoogleReviewEnhancementToggle();
  const toggleEnhancement = useToggleGoogleReviewEnhancement();
  
  // Calculate review analysis
  const reviewAnalysis = useMemo(() => {
    if (!businessSummary || !allReviews || allReviews.length === 0) return null;
    
    // Find earliest review date
    const reviewDates = allReviews
      .map(r => r.review_date ? new Date(r.review_date) : null)
      .filter((d): d is Date => d !== null)
      .sort((a, b) => a.getTime() - b.getTime());
    
    if (reviewDates.length === 0) return null;
    
    const earliestReview = reviewDates[0];
    const latestReview = reviewDates[reviewDates.length - 1];
    const today = new Date();
    
    // Calculate months between earliest review and today
    const monthsDiff = (today.getTime() - earliestReview.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
    const reviewsPerMonth = businessSummary.total_reviews / Math.max(monthsDiff, 1);
    
    // Estimate gym size (default to single operator gym - 100+ members)
    // This could be enhanced later with actual member count from database
    const estimatedGymSize = 100; // Single operator gym baseline
    const expectedReviewsPerMonth = estimatedGymSize * 0.1; // Assume 10% of members leave reviews per month (conservative)
    const expectedTotalReviews = expectedReviewsPerMonth * monthsDiff;
    const wastedTimeMonths = Math.max(0, (expectedTotalReviews - businessSummary.total_reviews) / expectedReviewsPerMonth);
    
    return {
      earliestReview,
      latestReview,
      monthsDiff,
      reviewsPerMonth,
      expectedReviewsPerMonth,
      expectedTotalReviews,
      wastedTimeMonths,
      actualTotalReviews: businessSummary.total_reviews,
    };
  }, [businessSummary, allReviews]);

  // Check if company has place_id (must be before any early returns)
  React.useEffect(() => {
    const checkPlaceId = async () => {
      if (!companyId) {
        setHasPlaceId(false);
        return;
      }
      setCheckingPlaceId(true);
      try {
        const { data, error } = await (supabase as any)
          .from('companies')
          .select('place_id')
          .eq('id', companyId)
          .single();
        
        if (error) {
          // If column doesn't exist (migration not applied), handle gracefully
          if (error.code === 'PGRST116' || error.message?.includes('does not exist') || error.message?.includes('column')) {
            console.warn('place_id column not found. Migrations may not be applied.');
            setHasPlaceId(false);
          } else {
            console.error('Error checking place_id:', error);
            setHasPlaceId(false);
          }
        } else {
          setHasPlaceId(!!(data as any)?.place_id);
        }
      } catch (error: any) {
        // Handle column not found gracefully
        if (error?.code === 'PGRST116' || error?.message?.includes('does not exist') || error?.message?.includes('column')) {
          console.warn('place_id column not found. Migrations may not be applied.');
          setHasPlaceId(false);
        } else {
          console.error('Error checking place_id:', error);
          setHasPlaceId(false);
        }
      } finally {
        setCheckingPlaceId(false);
      }
    };
    checkPlaceId();
  }, [companyId]);

  const handleRespondClick = (reviewUrl: string | null) => {
    if (reviewUrl) {
      window.open(reviewUrl, '_blank');
    }
  };

  const handleMarkResponded = async (reviewId: string) => {
    try {
      await markResponded.mutateAsync(reviewId);
    } catch (error) {
      console.error('Error marking review as responded:', error);
    }
  };

  const handleRefreshBusinessSummary = async () => {
    if (!companyId) return;

    setIsRefreshing(true);
    try {
      // Get place_id from company
      const { data: companyData, error: companyError } = await (supabase as any)
        .from('companies')
        .select('place_id')
        .eq('id', companyId)
        .single();

      if (companyError || !companyData?.place_id) {
        showToast('No place_id found. Please link your Google Business Profile first.', 'error');
        return;
      }

      // Get session token for authenticated edge function call
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        showToast('Authentication required. Please log in again.', 'error');
        return;
      }

      // Use fetch directly to ensure auth header is properly sent
      const functionUrl = `${process.env.REACT_APP_SUPABASE_URL || 'http://127.0.0.1:54321'}/functions/v1/fetch-google-business-summary`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          place_id: companyData.place_id,
          company_id: companyId,
          location_id: null,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Unknown error';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorText;
        } catch {
          errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`;
        }
        
        console.error('Error fetching Google Business summary:', {
          status: response.status,
          statusText: response.statusText,
          error: errorMessage,
        });
        
        const isTableNotFound = errorMessage.includes('does not exist') || errorMessage.includes('relation') || errorMessage.includes('table');
        if (isTableNotFound) {
          showToast('Database table not found. Migrations may not be applied.', 'error');
        } else if (response.status === 401) {
          showToast('Authentication failed. Please log out and log back in.', 'error');
        } else {
          showToast(`Failed to fetch business details: ${errorMessage}`, 'error');
        }
        return;
      }

      const fetchData = await response.json();

      if (fetchData?.error) {
        console.error('Edge Function returned error:', fetchData.error);
        const errorMessage = typeof fetchData.error === 'string' ? fetchData.error : fetchData.error?.message || 'Unknown error';
        const isTableNotFound = errorMessage.includes('does not exist') || errorMessage.includes('relation') || errorMessage.includes('table');
        if (isTableNotFound) {
          showToast('Database table not found. Migrations may not be applied.', 'error');
        } else {
          showToast(`Failed to fetch business details: ${errorMessage}`, 'error');
        }
        return;
      }

      // Only invalidate queries if edge function succeeded
      if (fetchData?.success) {
        // Small delay to ensure database write has completed
        await new Promise(resolve => setTimeout(resolve, 500));
        await queryClient.invalidateQueries({ queryKey: ['google-business-summary', companyId] });
        await queryClient.invalidateQueries({ queryKey: ['google-reviews', companyId] });
        showToast('Business details refreshed successfully!', 'success');
      } else {
        showToast('Failed to refresh business details', 'error');
      }
    } catch (error) {
      console.error('Error refreshing business summary:', error);
      showToast(`Failed to refresh: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleUnlinkGoogleBusiness = async () => {
    if (!companyId) return;

    if (!confirm('Are you sure you want to unlink your Google Business Profile? This will clear all review data.')) {
      return;
    }

    setIsRefreshing(true);
    try {
      // Delete all google_business_summary and google_reviews records
      await (supabase as any)
        .from('google_business_summary')
        .delete()
        .eq('company_id', companyId)
        .is('location_id', null);
      
      await (supabase as any)
        .from('google_reviews')
        .delete()
        .eq('company_id', companyId)
        .is('location_id', null);

      // Clear place_id from company
      const { error: updateError } = await (supabase as any)
        .from('companies')
        .update({ place_id: null } as any)
        .eq('id', companyId);

      if (updateError) {
        throw updateError;
      }

      // Invalidate queries to refresh UI
      await queryClient.invalidateQueries({ queryKey: ['google-business-summary', companyId] });
      await queryClient.invalidateQueries({ queryKey: ['google-reviews', companyId] });
      
      // Reset hasPlaceId state and trigger re-check
      setHasPlaceId(false);
      
      // Re-check place_id to update UI state
      setCheckingPlaceId(true);
      try {
        const { data: companyData } = await (supabase as any)
          .from('companies')
          .select('place_id')
          .eq('id', companyId)
          .single();
        setHasPlaceId(!!(companyData?.place_id));
      } catch {
        setHasPlaceId(false);
      } finally {
        setCheckingPlaceId(false);
      }

      showToast('Google Business Profile unlinked successfully', 'success');
    } catch (error) {
      console.error('Error unlinking Google Business Profile:', error);
      showToast(`Failed to unlink: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  if (checkingPlaceId) {
    return (
      <Card className="bg-[#221F1F] border-[#504A4A]">
        <CardContent className="py-12">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#9B1421]" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // If no place_id, show link button
  if (!businessSummary && !summaryLoading && hasPlaceId === false) {
    return (
      <Card className="bg-[#221F1F] border-[#504A4A]">
        <CardHeader>
          <CardTitle className="text-text-primary flex items-center gap-2">
            <Star className="w-5 h-5 text-text-primary" />
            Google Reviews
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Link2 className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-text-primary mb-2">
              Link Your Google Business Profile
            </h3>
            <p className="text-text-secondary mb-6">
              Update your address to automatically track reviews and ratings
            </p>
            <Button
              onClick={() => setShowAddressFlow(true)}
              className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
            >
              Link Google Business Profile
            </Button>
          </div>
        </CardContent>
        {showAddressFlow && (
          <AddressUpdateFlow
            onClose={() => setShowAddressFlow(false)}
            onSuccess={() => {
              setShowAddressFlow(false);
              // Refetch will happen automatically via React Query
            }}
          />
        )}
      </Card>
    );
  }

  // If has place_id but no summary (needs fetch)
  if (!businessSummary && !summaryLoading && hasPlaceId === true) {
    return (
      <Card className="bg-[#221F1F] border-[#504A4A]">
        <CardHeader>
          <CardTitle className="text-text-primary flex items-center gap-2">
            <Star className="w-5 h-5 text-text-primary" />
            Google Reviews
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <RefreshCw className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-text-primary mb-2">
              Fetch Business Details
            </h3>
            <div className="mb-4 p-3 bg-[#363232] rounded-lg border border-[#504A4A]">
              <div className="flex items-start gap-2 text-left">
                <Info className="w-4 h-4 text-text-tertiary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-text-secondary">
                  Your Google Business Profile is linked, but we need to fetch your business details and reviews. 
                  If this keeps failing, try unlinking and re-linking with a different address.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2 max-w-sm mx-auto">
              <Button
                onClick={handleRefreshBusinessSummary}
                disabled={isRefreshing}
                className="w-full bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
              >
                {isRefreshing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Fetching...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Fetch Business Details
                  </>
                )}
              </Button>
              <Button
                onClick={handleUnlinkGoogleBusiness}
                disabled={isRefreshing}
                className="w-full bg-[#363232] hover:bg-[#504A4A] text-text-secondary hover:text-text-primary border border-[#504A4A]"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Unlink & Start Over
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#221F1F] border-[#504A4A]">
      <CardHeader>
        <CardTitle className="text-text-primary flex items-center gap-2">
          <Star className="w-5 h-5 text-rollops-accent" />
          Google Reviews
        </CardTitle>
      </CardHeader>
      <CardContent>
        {summaryLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#9B1421]" />
          </div>
        ) : businessSummary ? (
          <>
            {/* Overall Rating */}
            <div className="mb-6 p-4 bg-[#363232] rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-3xl font-bold text-text-primary">
                    {businessSummary.overall_rating?.toFixed(1) || 'N/A'}
                  </div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-5 h-5 ${
                          star <= Math.round(businessSummary.overall_rating || 0)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-text-tertiary'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="text-text-secondary text-sm">
                    ({businessSummary.total_reviews} {businessSummary.total_reviews === 1 ? 'review' : 'reviews'})
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowIncorrectInfoModal(true)}
                    size="sm"
                    className="bg-[#363232] hover:bg-[#504A4A] text-text-secondary hover:text-text-primary text-xs border border-[#504A4A]"
                    title="Report incorrect business information"
                  >
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Incorrect Info?
                  </Button>
                  <Button
                    onClick={handleUnlinkGoogleBusiness}
                    size="sm"
                    className="bg-[#363232] hover:bg-[#504A4A] text-text-secondary hover:text-text-primary text-xs border border-[#504A4A]"
                    title="Unlink Google Business Profile"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Unlink
                  </Button>
                </div>
              </div>
              {businessSummary.business_name && (
                <div className="mt-2 text-sm text-text-primary">
                  {businessSummary.business_name}
                </div>
              )}
              {businessSummary.address && (
                <div className="mt-1 text-xs text-text-secondary">
                  {businessSummary.address}
                </div>
              )}
            </div>

            {/* Success Message - Show only if no actionable reviews and not perfect 5-star */}
            {(!reviews || reviews.length === 0) && 
             businessSummary.overall_rating && 
             businessSummary.overall_rating < 5.0 && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <p className="text-sm text-text-primary">
                    All caught up! No reviews need attention right now.
                  </p>
                </div>
              </div>
            )}

            {/* Review Analysis */}
            {reviewAnalysis && (
              <div className="mb-6 p-4 bg-[#363232] rounded-lg border border-[#504A4A]">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-rollops-accent" />
                  <h3 className="text-sm font-semibold text-text-primary">Review Performance Analysis</h3>
                </div>
                
                {/* Missing reviews alert at top */}
                {reviewAnalysis.wastedTimeMonths > 0 && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-red-400 flex-shrink-0" />
                      <div>
                        <div className="text-xs font-semibold text-red-400 mb-1">
                          Missing {Math.round(reviewAnalysis.wastedTimeMonths)} months of reviews
                        </div>
                        <div className="text-xs text-text-secondary">
                          Expected {Math.round(reviewAnalysis.expectedTotalReviews)} reviews, 
                          but only have {reviewAnalysis.actualTotalReviews}. 
                          At current rate, you&apos;re missing {Math.round(reviewAnalysis.wastedTimeMonths)} months worth of reviews.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Compact metrics below the alert */}
                <div className="grid grid-cols-4 gap-3 text-xs">
                  <div>
                    <div className="text-text-secondary text-xs mb-1">First Review</div>
                    <div className="text-text-primary font-medium text-sm">
                      {reviewAnalysis.earliestReview.toLocaleDateString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-text-secondary text-xs mb-1">Time Active</div>
                    <div className="text-text-primary font-medium text-sm">
                      {Math.round(reviewAnalysis.monthsDiff)} months
                    </div>
                  </div>
                  <div>
                    <div className="text-text-secondary text-xs mb-1">Reviews/Month</div>
                    <div className="text-text-primary font-medium text-sm">
                      {reviewAnalysis.reviewsPerMonth.toFixed(1)}
                    </div>
                  </div>
                  <div>
                    <div className="text-text-secondary text-xs mb-1">Expected/Month</div>
                    <div className="text-text-primary font-medium text-sm">
                      {reviewAnalysis.expectedReviewsPerMonth.toFixed(1)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recommendations Section - Lead with best stat (5-star rating) */}
            {(!reviews || reviews.length === 0) && businessSummary.overall_rating && businessSummary.overall_rating >= 4.5 && businessSummary.total_reviews < 100 && (
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-text-primary mb-2">
                      Great Ratings! Now Boost Your Visibility
                    </h3>
                    <p className="text-sm text-text-secondary">
                      You have excellent ratings ({businessSummary.overall_rating.toFixed(1)} stars), but you&apos;re missing out on 
                      potential customers because your review count is below 100. Most gyms have 30-50 members who would 
                      gladly leave a review. This is a quick win that can significantly improve your local search ranking.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Recommendations Section - Then show the issue (low review count) */}
            {(!reviews || reviews.length === 0) && businessSummary.total_reviews < 100 && (
              <div className="mb-6 p-4 bg-[#9B1421]/10 border border-[#9B1421]/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-text-primary flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-text-primary mb-2">
                      Boost Your Review Count
                    </h3>
                    <p className="text-sm text-text-secondary mb-3">
                      You have {businessSummary.total_reviews} reviews, but most successful gyms have 100+ reviews. 
                      With 30-50 active members, you could easily boost your visibility by asking satisfied students 
                      to leave reviews. Reviews are one of the most powerful factors for local search visibility.
                    </p>
                    <div className="text-xs text-text-tertiary">
                      💡 <strong>Quick Win:</strong> Send a text or email to your top 30-50 students asking them to 
                      share their experience. Most will be happy to help!
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actionable Reviews - Only show if there are reviews that need attention */}
            {reviewsLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#9B1421]" />
              </div>
            ) : reviews && reviews.length > 0 ? (
              <div className="space-y-3 mb-4">
                <h3 className="text-sm font-semibold text-text-primary">
                  ⚠️ {reviews.length} {reviews.length === 1 ? 'Review' : 'Reviews'} Need Attention
                </h3>
                {reviews.map((review) => (
                  <div
                    key={review.id}
                    className="p-4 bg-[#363232] rounded-lg border border-yellow-500/30"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= review.rating
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-text-tertiary'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-sm font-medium text-text-primary">
                          {review.reviewer_name}
                        </span>
                        {review.review_date && (
                          <span className="text-xs text-text-secondary">
                            {new Date(review.review_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {review.is_responded && (
                        <span className="text-xs text-green-400">Responded</span>
                      )}
                    </div>
                    {review.review_text && (
                      <p className="text-sm text-text-secondary mb-3">{review.review_text}</p>
                    )}
                    <div className="flex gap-2">
                      {review.google_review_url && (
                        <Button
                          onClick={() => handleRespondClick(review.google_review_url)}
                          size="sm"
                          className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white text-xs"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Respond on Google
                        </Button>
                      )}
                      {!review.is_responded && (
                        <Button
                          onClick={() => handleMarkResponded(review.id)}
                          size="sm"
                          className="bg-[#363232] hover:bg-[#504A4A] text-text-primary text-xs"
                          disabled={markResponded.isPending}
                        >
                          Mark as Responded
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                <div className="mt-4 p-4 bg-[#9B1421]/10 border border-[#9B1421]/30 rounded-lg">
                  <p className="text-sm text-text-secondary mb-3">
                    Need help managing your reviews or improving your online reputation? Our team can help you develop a strategy to boost your review count and respond effectively to feedback.
                  </p>
                  <Button
                    onClick={() => {
                      // Open email client or contact form
                      window.location.href = 'mailto:support@rollops.com?subject=Review Management Assistance';
                    }}
                    size="sm"
                    className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white text-xs"
                  >
                    Get Help with Review Management
                  </Button>
                </div>
              </div>
            ) : null}

            {/* RollOps Google Review Enhancement Feature */}
            <div className="mt-6 p-5 bg-gradient-to-br from-[#363232] to-[#2A2525] border-2 border-[#504A4A] rounded-lg">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#363232] rounded-full flex items-center justify-center border border-[#504A4A]">
                    <Star className="w-6 h-6 text-text-primary" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    RollOps Google Review Enhancement
                  </h3>
                  <p className="text-sm text-text-secondary mb-4 leading-relaxed">
                    Automatically detect your highest attending students and send them personalized review requests on Google once per year. 
                    If they&apos;ve already left a review, we&apos;ll ask them to provide a training and progress update - because all good reviews 
                    are like gold to combat the bad ones if they ever come in. We monitor daily for bad reviews and notify you immediately 
                    here so you can take decisive action.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <div className="text-xs text-text-primary bg-[#2A2525] px-2 py-1 rounded border border-[#504A4A]">
                      ✓ Auto-detects high attending students
                    </div>
                    <div className="text-xs text-text-primary bg-[#2A2525] px-2 py-1 rounded border border-[#504A4A]">
                      ✓ Annual review requests
                    </div>
                    <div className="text-xs text-text-primary bg-[#2A2525] px-2 py-1 rounded border border-[#504A4A]">
                      ✓ Progress update requests for existing reviews
                    </div>
                    <div className="text-xs text-text-primary bg-[#2A2525] px-2 py-1 rounded border border-[#504A4A]">
                      ✓ Daily bad review monitoring & alerts
                    </div>
                    <div className="text-xs text-text-primary bg-[#2A2525] px-2 py-1 rounded border border-[#504A4A]">
                      ✓ Google Reviews + RollOps Public
                    </div>
                  </div>
                  {/* Toggle for Google Review Enhancement */}
                  <div className="pt-4 border-t border-[#504A4A]">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex-1">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isEnhancementEnabled}
                            onChange={(e) => toggleEnhancement.mutate(e.target.checked)}
                            disabled={isLoadingToggle || toggleEnhancement.isPending}
                            className="w-5 h-5 rounded border-[#504A4A] bg-[#363232] text-rollops-accent focus:ring-[#9B1421] focus:ring-2 disabled:opacity-50 cursor-pointer"
                          />
                          <div>
                            <p className="text-sm font-semibold text-text-primary">
                              Enable Google Review Enhancement
                            </p>
                            <p className="text-xs text-text-secondary mt-1">
                              Automatically detect high-attending students and send annual review requests
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                    {isEnhancementEnabled && (
                      <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <p className="text-xs text-green-400">
                          ✓ Google Review Enhancement is active. Your high-attending students will receive annual review requests.
                        </p>
                      </div>
                    )}
                    <div className="pt-3 border-t border-[#504A4A]">
                      <p className="text-sm text-text-secondary mb-3">
                        Would you like more help managing your Google profile or lead generation?
                      </p>
                      <Button
                        onClick={() => {
                          window.location.href = 'mailto:support@rollops.pro?subject=Google Profile & Lead Generation Help';
                        }}
                        className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white text-sm"
                      >
                        Get Help with Google Profile & Lead Generation
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Legacy Feature Request */}
            <div className="mt-4 pt-4 border-t border-[#504A4A]">
              <FeatureRequestButton
                featureCategory="google_reviews"
                featureName="Supercharge - Auto-request reviews from students"
                description="Automatically send text/email to students asking for reviews after drop-in visits"
              />
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-text-secondary">
            <p>No Google Business Profile linked</p>
          </div>
        )}
      </CardContent>

      {/* Incorrect Info Modal */}
      {showIncorrectInfoModal && businessSummary && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary">Incorrect Business Profile?</h3>
              <button
                onClick={() => setShowIncorrectInfoModal(false)}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-text-secondary">
                The currently linked profile doesn&apos;t match your business? You can:
              </p>

              <div className="space-y-3">
                <div className="p-3 bg-[#363232] rounded-lg border border-[#504A4A]">
                  <p className="text-sm font-medium text-text-primary mb-1">Currently Linked:</p>
                  <p className="text-sm text-text-secondary">{businessSummary.business_name || 'Unknown'}</p>
                  {businessSummary.address && (
                    <p className="text-xs text-text-tertiary mt-1">{businessSummary.address}</p>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <Button
                    onClick={() => {
                      setShowIncorrectInfoModal(false);
                      setShowAddressFlow(true);
                    }}
                    className="w-full bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Re-link with Different Address
                  </Button>

                  <Button
                    onClick={() => {
                      const googleUrl = `https://www.google.com/maps/place/?q=place_id:${businessSummary.place_id}`;
                      window.open(googleUrl, '_blank');
                    }}
                    className="w-full bg-[#363232] hover:bg-[#504A4A] text-text-primary"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View on Google Maps
                  </Button>
                </div>

                <p className="text-xs text-text-tertiary mt-4">
                  💡 <strong>Tip:</strong> If multiple businesses have the same name, search by the exact address to find the correct one.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Address Update Flow */}
      {showAddressFlow && (
        <AddressUpdateFlow
          onClose={() => setShowAddressFlow(false)}
          onSuccess={() => {
            setShowAddressFlow(false);
            setShowIncorrectInfoModal(false);
            // Refetch will happen automatically via React Query
          }}
        />
      )}
    </Card>
  );
}
